import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  BannerWrapper,
  TabItem,
  TabList,
  DialogPosition,
  ActionModal,
} from '@ecdlink/ui';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/types';
import ROUTES from '@/routes/routes';
import { InfantProfileRouteState } from './index.types';
import { ProgressTab } from './progress-tab';
import { getInfantById } from '@/store/infant/infant.selectors';
import { VisitsTab } from './visits-tab';
import { getJoyrideStyles } from '@/components/walkthrough/styles';
import { Tooltip } from '@/components/walkthrough/tooltip';
import Joyride, { Step } from 'react-joyride';
import { useWalkthrough } from '@/context/walkthroughHookContext';
import {
  WalkthroughInfoPage,
  WalkthroughInfoPageProps,
} from '@/components/walkthrough/info-page';
import { contactSteps } from './contact-tab/walkthrough/steps';
import { ReferralsTab } from './referrals-tab';
import { ContactTab } from './contact-tab';
import { visitSteps } from './visits-tab/walkthrough/steps';
import { SuccessCard } from '@/components/success-card/success-card';
import { ReactComponent as AwardIcon } from '@/assets/awardIcon.svg';
import {
  Document,
  InfantDto,
  getStringFromClassNameOrId,
  replaceBraces,
  useDialog,
  usePrevious,
} from '@ecdlink/core';
import { progressSteps } from './progress-tab/walkthrough/steps';
import { infantActions, infantThunkActions } from '@/store/infant';
import { useAppDispatch } from '@/store';
import { referralsSteps } from './referrals-tab/walkthrough/steps';
import { ReactComponent as PollyNeutral } from '@/assets/pollyNeutral.svg';
import { InfantModelInput } from '@/../../../packages/graphql/lib';
import { documentActions, documentThunkActions } from '@/store/document';
import { FileTypeEnum, WorkflowStatusEnum } from '@ecdlink/graphql';
import { newGuid } from '@/utils/common/uuid.utils';
import { InfantRoadToHealthModel } from '@/schemas/infant/infant-road-to-health';
import { useStaticData } from '@/hooks/useStaticData';
import { userSelectors } from '@/store/user';
import { InfantRoadToHealth } from '../components/infant-road-to-health/infant-road-to-health';
import {
  notificationActions,
  notificationsSelectors,
} from '@store/notifications';
import { notificationTagConfig } from '@/constants/notifications';
import {
  ClientTabIndex,
  clientTabKeyMap,
  ClientTabTypes,
} from '@/pages/client/clients-tab/filters';
import {
  DUMMY_INFANT_FIRST_NAME,
  dummyInfantWalkthroughLabel,
} from './walkthrough/profile-dummy';

export const InfantProfile: React.FC = () => {
  const [isInfoPage, setIsInfoPage] = useState(false);
  const user = useSelector(userSelectors.getUser);
  const { getWorkflowStatusIdByEnum, getDocumentTypeIdByEnum } =
    useStaticData();

  const {
    handleCallback,
    walkthroughDispatch,
    walkthroughState,
    walkthroughStepIndex,
    setIsWalkthroughSession,
  } = useWalkthrough();

  const appDispatch = useAppDispatch();

  const { state } = useLocation<InfantProfileRouteState>();

  const previousActiveTab = usePrevious(state?.activeTabIndex);

  const showWelcomeDialog = state?.displayHelp || false;

  const { isOnline } = useOnlineStatus();

  const dialog = useDialog();
  const history = useHistory();
  const location = useLocation();

  const [, , , infantId] = location.pathname.split('/');

  const infant = useSelector((state: RootState) =>
    getInfantById(state, infantId)
  );

  const addRoadToHealthNotifications = useSelector(
    notificationsSelectors.getAllNotifications
  ).filter(
    (item) =>
      item?.message?.cta?.includes(
        notificationTagConfig?.RoadToHealthBook.cta ?? ''
      ) && item?.message?.routeConfig?.route.includes(infantId)
  );

  const roadToHealthBook = state?.isRoadToHealthBook;

  const infantName = useMemo(
    () => infant?.firstName ?? infant?.user?.firstName ?? '',
    [infant]
  );
  const infantCaregiver = useMemo(
    () => infant?.caregiver || infant?.mother?.user,
    [infant]
  );
  const caregiverName = useMemo(
    () => infantCaregiver?.firstName || '',
    [infantCaregiver?.firstName]
  );

  const tabItems: TabItem[] = [
    {
      title: ClientTabTypes.Visits,
      initActive: true,
      index: ClientTabIndex.VISITS,
      child: <VisitsTab />,
    },
    {
      title: ClientTabTypes.Progress,
      initActive: false,
      index: ClientTabIndex.PROGRESS,
      child: <ProgressTab />,
    },
    {
      title: ClientTabTypes.Referrals,
      initActive: false,
      index: ClientTabIndex.REFERRALS,
      child: <ReferralsTab />,
    },
    {
      title: ClientTabTypes.Contact,
      index: ClientTabIndex.CONTACT,
      initActive: false,
      child: <ContactTab />,
    },
  ];

  const {
    steps,
    infoPageSection,
    hideJoyRideBorders,
    infoPageTitle,
    displayExtraComponent,
  } = useMemo((): {
    steps: Step[];
    infoPageTitle: string;
    infoPageSection: WalkthroughInfoPageProps['sectionName'];
    hideJoyRideBorders?: boolean;
    displayExtraComponent?: boolean;
  } => {
    switch (state?.activeTabIndex ?? 0) {
      case ClientTabIndex.CONTACT:
        return {
          steps: contactSteps,
          infoPageTitle: 'Contact',
          infoPageSection: 'Contact',
          hideJoyRideBorders: walkthroughStepIndex === 3,
        };
      case ClientTabIndex.PROGRESS:
        return {
          steps: progressSteps,
          infoPageTitle: 'Client progress summary',
          infoPageSection: 'Client progress summary',
          hideJoyRideBorders: walkthroughStepIndex === 2,
        };
      case ClientTabIndex.REFERRALS:
        return {
          steps: referralsSteps,
          infoPageTitle: 'Referrals',
          infoPageSection: 'Referrals',
          hideJoyRideBorders: walkthroughStepIndex === 3,
        };
      default:
        return {
          steps: visitSteps,
          infoPageSection: 'Child client visits',
          infoPageTitle: 'Child client visits',
          hideJoyRideBorders: walkthroughStepIndex === 3,
          displayExtraComponent: true,
        };
    }
  }, [state?.activeTabIndex, walkthroughStepIndex]);

  const onHelp = useCallback(() => {
    setIsInfoPage(false);
    setIsWalkthroughSession('true');
    walkthroughDispatch?.({ type: 'SET_TOUR_ACTIVE', payload: true });
    setTimeout(
      () => walkthroughDispatch?.({ type: 'SET_TOUR_ACTIVE', payload: true }),
      200
    );
  }, [setIsWalkthroughSession, walkthroughDispatch]);

  const updateClickedTab = useCallback(() => {
    if (!infant) return;

    const prev = {
      clickedProgressTab: infant.clickedProgressTab ?? false,
      clickedContactTab: infant.clickedContactTab ?? false,
      clickedReferralsTab: infant.clickedReferralsTab ?? false,
      clickedVisitTab: infant.clickedVisitTab ?? false,
    };

    let next: typeof prev = { ...prev };

    switch (state?.activeTabIndex) {
      case ClientTabIndex.PROGRESS:
        next.clickedProgressTab = true;
        break;

      case ClientTabIndex.REFERRALS:
        next.clickedReferralsTab = true;
        break;

      case ClientTabIndex.CONTACT:
        next.clickedContactTab = true;
        break;

      default:
        next.clickedVisitTab = true;
        break;
    }

    // Detect ONLY false → true changes
    const changedKeys = (Object.keys(next) as (keyof typeof next)[]).filter(
      (key) => prev[key] === false && next[key] === true
    );

    // Abort if nothing changed
    if (!changedKeys.length) return;

    // update the state
    changedKeys.forEach((key) => {
      const mappedTab = clientTabKeyMap[key];
      appDispatch(
        infantActions.setInfantTabClicked({
          infantId: infantId,
          tab: mappedTab,
          clicked: next[key]!,
          isOnline: isOnline,
        })
      );
    });

    // Send ONLY changed fields to backend
    if (!isOnline) return;

    const input = changedKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Partial<typeof next>);

    const updateInput = input as InfantModelInput;
    updateInput.dateOfBirth = infant.user?.dateOfBirth;

    appDispatch(
      infantThunkActions.updateInfant({ input: updateInput, id: infantId })
    );
  }, [appDispatch, infant, infantId, state?.activeTabIndex, isOnline]);

  const handleWelcomeDialog = useCallback(() => {
    const currentTab = state?.activeTabIndex ?? 0;
    if (
      (currentTab === ClientTabIndex.VISITS && !infant?.clickedVisitTab) ||
      (currentTab === ClientTabIndex.PROGRESS && !infant?.clickedProgressTab) ||
      (currentTab === ClientTabIndex.REFERRALS &&
        !infant?.clickedReferralsTab) ||
      (currentTab === ClientTabIndex.CONTACT && !infant?.clickedContactTab) ||
      showWelcomeDialog
    ) {
      let tabName = '';

      switch (currentTab) {
        case ClientTabIndex.PROGRESS:
          tabName = 'progress';
          break;
        case ClientTabIndex.REFERRALS:
          tabName = 'referrals';
          break;
        case ClientTabIndex.CONTACT:
          tabName = 'contact';
          break;

        default:
          tabName = 'visits';
          break;
      }

      return dialog({
        position: DialogPosition.Middle,
        color: 'bg-white',
        render(onClose) {
          return (
            <ActionModal
              customIcon={<PollyNeutral className="mb-3 h-24 w-24" />}
              title={` Welcome to the ${tabName} tab!`}
              detailText="Can I show you how this works?"
              actionButtons={[
                {
                  colour: 'primary',
                  text: 'Yes, help me!',
                  textColour: 'white',
                  type: 'filled',
                  leadingIcon: 'CheckCircleIcon',
                  onClick: () => {
                    updateClickedTab();
                    setIsWalkthroughSession('true');
                    onHelp();
                    onClose();
                  },
                },
                {
                  colour: 'primary',
                  text: 'No, skip',
                  textColour: 'primary',
                  type: 'outlined',
                  leadingIcon: 'ClockIcon',
                  onClick: () => {
                    updateClickedTab();
                    onClose();
                  },
                },
              ]}
            />
          );
        },
      });
    }
  }, [
    dialog,
    infant?.clickedContactTab,
    infant?.clickedProgressTab,
    infant?.clickedReferralsTab,
    infant?.clickedVisitTab,
    onHelp,
    setIsWalkthroughSession,
    showWelcomeDialog,
    state?.activeTabIndex,
    updateClickedTab,
  ]);

  const goBack = useCallback(() => {
    if (isInfoPage) {
      return setIsInfoPage(false);
    }

    return history.push(ROUTES.CLIENTS.ROOT);
  }, [history, isInfoPage]);

  const onSubmit = useCallback(
    async (data: InfantRoadToHealthModel) => {
      if (infant?.user?.id) {
        const fileName = 'roadtohealthbook.png';
        const workflowStatusId = getWorkflowStatusIdByEnum(
          WorkflowStatusEnum.DocumentPendingVerification
        );
        const documentTypeId = getDocumentTypeIdByEnum(
          FileTypeEnum.RoadToHealthBook
        );
        const documentInputModel: Document = {
          id: newGuid(),
          userId: infant?.user.id,
          createdUserId: user?.id ?? '',
          workflowStatusId: workflowStatusId ?? '',
          documentTypeId: documentTypeId ?? '',
          name: fileName,
          fileName: fileName,
          file: data?.roadToHealthBook,
          fileType: FileTypeEnum.RoadToHealthBook,
        };

        appDispatch(documentActions.createDocument(documentInputModel));
        if (isOnline) {
          await appDispatch(
            documentThunkActions.createDocument(documentInputModel)
          ).unwrap();
        }

        const input: InfantDto = {
          id: infantId,
          dateOfBirth: infant?.user?.dateOfBirth,
          firstName: infant?.firstName,
          genderId: infant?.genderId,
          userId: infant?.user.id ?? '',
          weightAtBirth: Number(data?.weightAtBirth),
          lengthAtBirth: Number(data?.lengthAtBirth),
        };

        appDispatch(infantActions.updateInfantRTHDetails(input));
        if (isOnline) {
          appDispatch(
            infantThunkActions.updateInfant({
              id: infantId,
              input: {
                dateOfBirth: infant?.user?.dateOfBirth,
                weightAtBirth: data?.weightAtBirth?.toString(),
                lengthAtBirth: data?.lengthAtBirth?.toString(),
              },
            })
          );
        }

        if (addRoadToHealthNotifications) {
          addRoadToHealthNotifications.forEach((x) => {
            appDispatch(notificationActions.removeNotification(x!));
          });
        }

        goBack();
      }
    },
    [
      addRoadToHealthNotifications,
      appDispatch,
      getDocumentTypeIdByEnum,
      getWorkflowStatusIdByEnum,
      goBack,
      infant?.firstName,
      infant?.genderId,
      infant?.user?.dateOfBirth,
      infantId,
      isOnline,
      user?.id,
    ]
  );

  const renderContent = useMemo(() => {
    if (roadToHealthBook && !isInfoPage) {
      return (
        <div className="mx-4">
          <InfantRoadToHealth
            infantDetails={{
              firstName: infant?.firstName ?? infant?.user?.firstName,
            }}
            onSubmit={onSubmit}
            isFromClientProfile={true}
          />
        </div>
      );
    }

    return isInfoPage ? (
      <WalkthroughInfoPage
        sectionName={infoPageSection}
        onHelp={onHelp}
        onClose={goBack}
        extraElement={
          displayExtraComponent ? (
            <SuccessCard
              className="my-4"
              customIcon={<AwardIcon className="h-14	w-14" />}
              text={`You can earn points with every visit!`}
              textColour="successDark"
              color="successBg"
            />
          ) : (
            <></>
          )
        }
      />
    ) : (
      <TabList
        id={getStringFromClassNameOrId(visitSteps[0].target)}
        tabClassName="min-w-0 w-24"
        className="bg-uiBg border-uiLight fixed z-20 w-full border-b"
        tabItems={tabItems}
        setSelectedIndex={state?.activeTabIndex ?? 0}
        tabSelected={(tab) =>
          history.push(location.pathname, { activeTabIndex: tab.index })
        }
      />
    );
  }, [
    displayExtraComponent,
    goBack,
    history,
    infant?.firstName,
    infant?.user?.firstName,
    infoPageSection,
    isInfoPage,
    location.pathname,
    onHelp,
    onSubmit,
    roadToHealthBook,
    state?.activeTabIndex,
    tabItems,
  ]);

  useLayoutEffect(() => {
    if (isOnline) {
      (async () =>
        appDispatch(
          infantThunkActions.getInfantVisits({ infantId })
        ).unwrap())();
    }
  }, [appDispatch, infantId, isOnline]);

  useEffect(() => {
    if (previousActiveTab !== state?.activeTabIndex || showWelcomeDialog) {
      handleWelcomeDialog();
    }
  }, [
    handleWelcomeDialog,
    previousActiveTab,
    showWelcomeDialog,
    state?.activeTabIndex,
  ]);

  return (
    <>
      <Joyride
        steps={steps.map((item) => ({
          ...item,
          // Always use dummy name in tour copy so content is not caseload-specific.
          content: replaceBraces(
            String(item?.content),
            walkthroughState?.isTourActive
              ? DUMMY_INFANT_FIRST_NAME
              : infantName || ''
          ),
        }))}
        run={walkthroughState?.isTourActive}
        stepIndex={walkthroughStepIndex}
        callback={handleCallback}
        continuous={true}
        tooltipComponent={({ ...props }) => (
          <Tooltip
            {...props}
            pollyInformationalSteps={[0, 2]}
            pollyNeutralSteps={[1]}
            pollyImpressedSteps={[3]}
            displayCloseButton={props.index < props.size - 1}
          />
        )}
        styles={getJoyrideStyles(hideJoyRideBorders)}
      />
      <BannerWrapper
        size="medium"
        renderBorder
        onBack={goBack}
        title={
          isInfoPage
            ? infoPageTitle
            : walkthroughState?.isTourActive
            ? dummyInfantWalkthroughLabel
            : `${!!caregiverName ? caregiverName + ' &' : ''} ${infantName}`
        }
        backgroundColour="white"
        displayOffline={!isOnline}
        displayHelp={!isInfoPage}
        onHelp={() => setIsInfoPage(true)}
      >
        {renderContent}
        <div id="walkthrough-last-step" className="w-full"></div>
      </BannerWrapper>
    </>
  );
};
